import { WebglHelper } from './webglHelper';
import { Webgl2Pipeline } from './webgl2Pipeline';
export class SoftMax {

    private vbWebgl2: Webgl2Pipeline;
    private vbHelper: WebglHelper;
    private frameBuffer: WebGLFramebuffer;
    private fragmentShader: WebGLShader;
    private inputTexture: WebGLTexture;
    private gl: WebGL2RenderingContext;
    private program: WebGLProgram;
    private segmentationMaskWidth: number;
    private segmentationMaskHeight: number;

    /**
     * @function create
     * @static
     * @public
     * @param {Webgl2Pipeline} vbWebgl2 The instance of the webgl2 class
     * @returns {SoftMax} Initialization of the class
     */
    public static create(vbWebgl2: Webgl2Pipeline): SoftMax { return new SoftMax(vbWebgl2); }

    private constructor(vbWebgl2: Webgl2Pipeline) {
        this.vbWebgl2 = vbWebgl2;
        this.vbHelper = WebglHelper.create();
        this.segmentationMaskWidth = this.vbWebgl2.segmentationMaskCanvas.width;
        this.segmentationMaskHeight = this.vbWebgl2.segmentationMaskCanvas.height;

        this.gl = this.vbWebgl2.gl;

        const fragmentShaderSource = String.raw`#version 300 es
        precision highp float;
    
        // Passed in from the vertex shader.
        in vec2 v_texCoord;
    
        out vec4 outColor;
    
        uniform sampler2D u_inputSegmentation;
    
        void main() {
            // The softMax equation to retrieve the maximum of the 2 values returned by the AI for each pixel.
            // We pass this value in our alpha afterwards to obtain the mask
            vec2 segmentation = texture(u_inputSegmentation, v_texCoord).rg;
            float shift = max(segmentation.r, segmentation.g);
            float backgroundExp = exp(segmentation.r - shift);
            float personExp = exp(segmentation.g - shift);
            outColor = vec4(vec3(0.0), personExp / (backgroundExp + personExp));
        }`;

        // Compilation of the shader fragment
        this.fragmentShader = this.vbHelper.initShader(this.gl, this.gl.FRAGMENT_SHADER, fragmentShaderSource);
        // Compiling the program with the fragmentshader compiled above and the vertexshader of the virtualBackgroundWebGL2Pipeline class
        this.program = this.vbHelper.initProgram(this.gl, this.vbWebgl2.vertexShader, this.fragmentShader, this.vbWebgl2.positionBuffer, this.vbWebgl2.texCoordBuffer);

        // Creation of the texture that will contain the mask of the person
        this.inputTexture = this.vbHelper.createTexture(this.gl, this.gl.RG32F, this.segmentationMaskWidth, this.segmentationMaskHeight);

        this.frameBuffer = this.gl.createFramebuffer();
        this.gl.bindFramebuffer(this.gl.FRAMEBUFFER, this.frameBuffer);
        this.gl.framebufferTexture2D(this.gl.DRAW_FRAMEBUFFER, this.gl.COLOR_ATTACHMENT0, this.gl.TEXTURE_2D, this.vbWebgl2.segmentationTexture, 0);

        const inputLocation = this.gl.getUniformLocation(this.program, 'u_inputSegmentation');
        this.gl.useProgram(this.program);
        this.gl.uniform1i(inputLocation, 1);
    }

    /**
     * @function render
     * @public
     * @description applies the softmax formula to each given image
     */
    public render() {
        const tflite = this.vbWebgl2.tflite;
        const outputMemoryOffset = tflite._getOutputMemoryOffset() / 4;

        this.gl.viewport(0, 0, this.segmentationMaskWidth, this.segmentationMaskHeight);
        this.gl.useProgram(this.program);
        this.gl.activeTexture(this.gl.TEXTURE1);
        this.gl.bindTexture(this.gl.TEXTURE_2D, this.inputTexture);
        this.gl.texSubImage2D(this.gl.TEXTURE_2D, 0, 0, 0, this.segmentationMaskWidth, this.segmentationMaskHeight, this.gl.RG, this.gl.FLOAT, tflite.HEAPF32, outputMemoryOffset);
        this.gl.bindFramebuffer(this.gl.FRAMEBUFFER, this.frameBuffer);
        this.gl.drawArrays(this.gl.TRIANGLE_STRIP, 0, 4);

    }

    /**
     * @function cleanUp
     * @public
     * @description removes the different components used to realize the softmax equation
     */
    public cleanUp() {
        if (this.inputTexture) this.gl.deleteTexture(this.inputTexture);
        if (this.frameBuffer) this.gl.deleteFramebuffer(this.frameBuffer);
        if (this.program) this.gl.deleteProgram(this.program);
        if (this.fragmentShader) this.gl.deleteShader(this.fragmentShader);
    }

}
